import { useCallback, useEffect, useRef, useState } from 'react';
import { useContactVerification } from './contact-react.js';
import { submitVerifiedPhone } from './verified-phone-kyc.js';
/** No phone number is stored in query state, telemetry or persistence. */
export function useVerifiedPhoneKyc(client, provider, customerId, onComplete) {
  const verification = useContactVerification(client, false, 'phone');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const active = useRef(null);
  const completed = useRef(false);
  const attempted = useRef(false);
  useEffect(() => {
    attempted.current = false;
    completed.current = false;
    setBusy(false);
    setError(false);
    const stop = () => {
      active.current?.abort();
      active.current = null;
    };
    const unsubscribe = client.onDispose(stop);
    return () => {
      unsubscribe();
      stop();
    };
  }, [client]);
  const submit = useCallback(async () => {
    if (active.current || completed.current) return;
    const controller = new AbortController();
    active.current = controller;
    setBusy(true);
    setError(false);
    try {
      const next = await submitVerifiedPhone(client, provider, customerId, controller.signal);
      if (next.requirements.phoneNumber?.status === 'incomplete') throw new Error('phone_verification_pending');
      if (!controller.signal.aborted) {
        const refreshed = await onComplete();
        if (!controller.signal.aborted) {
          completed.current = refreshed;
          setError(!refreshed);
        }
      }
    } catch {
      if (!controller.signal.aborted) {
        setError(true);
        verification.retry();
        client.observeFunnel?.({
          stage: 'phone_code',
          outcome: 'error',
          errorCode: 'verification_failed'
        });
      }
    } finally {
      if (active.current === controller) active.current = null;
      if (!controller.signal.aborted) setBusy(false);
    }
  }, [client, provider, customerId, onComplete, verification.retry]);
  useEffect(() => {
    if (verification.step !== 'ready') {
      attempted.current = false;
      return;
    }
    if (verification.loading || attempted.current) return;
    attempted.current = true;
    void submit();
  }, [verification.step, verification.loading, submit]);
  return {
    verification,
    busy,
    error,
    submit
  };
}